import { useMemo, useState } from "react";
import { t } from "../../shared/trendReportShared";
import LanguagePicker from "./LanguagePicker";

// One list that answers "if I press send, where does it go?".
//
// Groups and the people they resolve to used to be two stacked cards, so a
// group of twelve appeared as one row up top and twelve more rows below - the
// same recipients counted twice, which read as two competing lists. Here a
// group is a single row you expand to see its members nested underneath, so
// nobody appears in two places.
//
// Membership is re-resolved before every send (see
// backend/domains/trend_reports/news_letter/subscribers.py), which is why a
// group row shows a live member count rather than a frozen list.
//
// Opted-out people sit in their own collapsed section rather than greyed out
// in place. They are not pending work - nobody should act on them - but they
// must stay visible, because an unsubscribed row is also what stops a group
// sync from silently re-adding that person.

function SourceBadge({ sub, language }) {
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      <span
        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
          sub.source_group
            ? "bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-400"
            : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
        }`}
      >
        {sub.source_group || t("newsletterSourceManual", language)}
      </span>
      {sub.is_external && (
        <span className="inline-flex items-center rounded-full bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide">
          {t("newsletterExternal", language)}
        </span>
      )}
    </div>
  );
}

// `nested` members already sit under a labelled group row, so repeating the
// group name on every one of them would be noise.
function PersonRow({
  sub,
  language,
  busyId,
  onSetLanguage,
  onRemove,
  nested = false,
  muted = false,
}) {
  return (
    <li
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${
        muted
          ? "bg-transparent border-dashed border-gray-200 dark:border-gray-700 opacity-70"
          : nested
            ? "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/60"
            : "bg-gray-50 dark:bg-gray-900/40 border-gray-100 dark:border-gray-700/60"
      }`}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
          {sub.display_name || sub.email}
        </p>
        <p className="text-[11px] text-gray-400 truncate">{sub.email}</p>
        {!nested && <SourceBadge sub={sub} language={language} />}
      </div>
      <div className="shrink-0 flex items-center gap-4">
        {!muted && (
          <LanguagePicker
            value={sub.language}
            language={language}
            size="xs"
            inlineLabel={t("newsletterRowLanguage", language)}
            disabled={busyId === sub.id}
            onChange={(lang) => onSetLanguage(sub, lang)}
          />
        )}
        <button
          type="button"
          onClick={() => onRemove(sub)}
          disabled={busyId === sub.id}
          className="text-[11px] font-semibold text-gray-400 hover:text-red-500 disabled:opacity-40"
        >
          {t("priorityRemove", language)}
        </button>
      </div>
    </li>
  );
}

export default function AudiencePanel({
  language,
  audiences,
  subscribers,
  syncing,
  busyGroup,
  busyId,
  onAddClick,
  onSetGroupLanguage,
  onRemoveGroup,
  onSetSubscriberLanguage,
  onRemoveSubscriber,
  onSync,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [showOptedOut, setShowOptedOut] = useState(false);

  const toggle = (groupId) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(groupId)) next.add(groupId);
      return next;
    });

  // A person is filed under the group that put them there. Editing one member
  // clears their source_group server-side to pin them, so they move down into
  // "added individually" - which is exactly what being pinned means.
  const { groups, individuals, optedOut, activeCount, emailCount } =
    useMemo(() => {
      const items = subscribers.items || [];
      const active = items.filter((s) => s.is_active);
      const auds = audiences.items || [];

      const byGroup = new Map(auds.map((a) => [a.group_id, []]));
      const solo = [];
      for (const s of active) {
        const bucket = s.source_group ? byGroup.get(s.source_group) : null;
        if (bucket) bucket.push(s);
        else solo.push(s);
      }

      return {
        groups: auds.map((a) => ({
          ...a,
          members: byGroup.get(a.group_id) || [],
        })),
        individuals: solo,
        optedOut: items.filter((s) => !s.is_active),
        activeCount: active.length,
        // A 'both' recipient is two separate emails; see dispatch.languages_for.
        emailCount: active.reduce(
          (n, s) => n + (s.language === "both" ? 2 : 1),
          0,
        ),
      };
    }, [subscribers.items, audiences.items]);

  const loading =
    audiences.status === "loading" || subscribers.status === "loading";
  const failed = audiences.status === "error" || subscribers.status === "error";
  const empty =
    !loading &&
    !failed &&
    groups.length === 0 &&
    individuals.length === 0 &&
    optedOut.length === 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400">
          {t("newsletterWhoGets", language)}
        </h3>
        {activeCount > 0 && (
          <span className="text-[11px] text-gray-400">
            {activeCount} {t("newsletterPeopleCount", language)}
            {emailCount !== activeCount &&
              ` · ${emailCount} ${t("newsletterEmailsCount", language)}`}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4">
        {t("newsletterWhoGetsHint", language)}
      </p>

      {loading && (
        <p className="text-xs text-gray-400">
          {t("newsletterLoading", language)}
        </p>
      )}
      {failed && (
        <p className="text-xs text-red-500">{t("newsletterError", language)}</p>
      )}
      {empty && (
        <p className="text-xs text-gray-400">
          {t("newsletterNoOne", language)}
        </p>
      )}

      {groups.length > 0 && (
        <ul className="space-y-2">
          {groups.map((g) => {
            const open = expanded.has(g.group_id);
            return (
              <li
                key={g.group_id}
                className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 overflow-hidden"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => toggle(g.group_id)}
                    aria-expanded={open}
                    className="flex items-center gap-2 min-w-0 text-left group"
                  >
                    <svg
                      className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform ${
                        open ? "rotate-90" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100 truncate group-hover:text-orange-600 dark:group-hover:text-orange-400">
                        {g.group_id}
                      </span>
                      <span className="block text-[11px] text-gray-400">
                        {g.active_count}
                        {g.active_count !== g.member_count &&
                          `/${g.member_count}`}{" "}
                        {t("newsletterMembers", language)} ·{" "}
                        {t(
                          open
                            ? "newsletterHideMembers"
                            : "newsletterShowMembers",
                          language,
                        )}
                      </span>
                    </span>
                  </button>
                  <div className="shrink-0 flex items-center gap-2">
                    <LanguagePicker
                      value={g.language}
                      language={language}
                      inlineLabel={t("newsletterRowLanguage", language)}
                      disabled={busyGroup === g.group_id}
                      onChange={(lang) => onSetGroupLanguage(g.group_id, lang)}
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveGroup(g.group_id)}
                      disabled={busyGroup === g.group_id}
                      className="text-[11px] font-semibold text-gray-400 hover:text-red-500 disabled:opacity-40"
                    >
                      {t("priorityRemove", language)}
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-gray-100 dark:border-gray-700/60 px-3 py-2.5">
                    {g.members.length === 0 ? (
                      <p className="text-xs text-gray-400">
                        {t("newsletterEmptyGroup", language)}
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        <li className="text-[11px] text-gray-400 leading-relaxed pb-1">
                          {t("newsletterMemberLanguageHint", language)}
                        </li>
                        {g.members.map((sub) => (
                          <PersonRow
                            key={sub.id}
                            sub={sub}
                            language={language}
                            busyId={busyId}
                            onSetLanguage={onSetSubscriberLanguage}
                            onRemove={onRemoveSubscriber}
                            nested
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {individuals.length > 0 && (
        <div className={groups.length > 0 ? "mt-4" : ""}>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <h4 className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
              {t("newsletterIndividuals", language)}
            </h4>
            <span className="text-[10px] text-gray-400">
              {t("newsletterIndividualsHint", language)}
            </span>
          </div>
          <ul className="space-y-2">
            {individuals.map((sub) => (
              <PersonRow
                key={sub.id}
                sub={sub}
                language={language}
                busyId={busyId}
                onSetLanguage={onSetSubscriberLanguage}
                onRemove={onRemoveSubscriber}
              />
            ))}
          </ul>
        </div>
      )}

      {optedOut.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60">
          <button
            type="button"
            onClick={() => setShowOptedOut((o) => !o)}
            className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showOptedOut ? "rotate-90" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            {t("newsletterShowOptedOut", language)} ({optedOut.length})
          </button>
          {showOptedOut && (
            <ul className="space-y-2 mt-3">
              {optedOut.map((sub) => (
                <PersonRow
                  key={sub.id}
                  sub={sub}
                  language={language}
                  busyId={busyId}
                  onSetLanguage={onSetSubscriberLanguage}
                  onRemove={onRemoveSubscriber}
                  muted
                />
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={onAddClick}
          className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-orange-500/50 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
        >
          + {t("newsletterAddPeople", language)}
        </button>
        <button
          type="button"
          onClick={onSync}
          disabled={syncing || groups.length === 0}
          className="sm:w-auto py-2.5 px-4 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 disabled:opacity-40 disabled:hover:text-gray-500 transition-colors"
        >
          {syncing
            ? t("newsletterSyncing", language)
            : `↻ ${t("newsletterSyncNow", language)}`}
        </button>
      </div>
    </div>
  );
}
